import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { forkJoin, of, Observable } from 'rxjs';
import { Person, PersonResponcibilityList } from 'src/app/shared/person';
import { ClientApiService } from 'src/app/services/client-api.service';
import { parseRegisterName, normalizeForMatch } from 'src/app/shared/register-data-parsing';

type DirectorQuickSetState = 'none' | 'create-offer' | 'update-offer' | 'confirmed' | 'picker' | 'saving';

/** Whether `recompute()` could confidently decide a single write target on
 *  its own. 'uncertain' is what routes to the `'picker'` state instead of
 *  auto-firing from `confirmPending()` — see AG-23d. */
type MatchConfidence = 'confident' | 'uncertain';

/** One row in the picker's dropdown — `person: null` is the fixed
 *  "insert new" option, always present. */
interface PickerOption {
  label: string;
  person: Person | null;
}

/**
 * AG-23c — offers to create or update the company's director/CEO/owner
 * (`Person.PERSRESPONSIBLE = 2`) from a name extracted by a company-register
 * lookup (`app-company-register-verify`'s `director` field). Never writes on
 * its own initiative — every write is a manager click. See
 * docs/PROMPT_company-register-editclient-integration.md for the base
 * design.
 *
 * AG-23d — `confirmPending()` (called by `edit-client.component.ts` right
 * after the register-verify "Оновити" click, PR #149's "one confirm, two
 * calls") only auto-fires when `recompute()` found an unambiguous target
 * (`confidence === 'confident'`). Everything else routes to the `'picker'`
 * state: an explicit, manager-driven choice over every existing person on
 * the client (not just ones already flagged `PERSRESPONSIBLE = 2`), with
 * its own separate confirm — because the auto-detect that PR #149 fires
 * immediately on is exactly what can miss an existing person filed under a
 * different name/title. Every match comparison is exact string equality
 * after normalization (`normalizeForMatch`) — never a fuzzy/similarity
 * score. See docs/PROMPT_company-register-apply-refinements.md.
 *
 * `PERSID: -2` is this codebase's existing new-record sentinel for `Person`
 * (see `list-client-persons.component.ts`'s `onAddRow`/clone-row handling and
 * `edit-person.component.ts`) — the backend assigns the real `PERSID` on
 * `postPersonById`.
 */
@Component({
  selector: 'app-company-director-quick-set',
  templateUrl: './company-director-quick-set.component.html',
  styleUrls: ['./company-director-quick-set.component.scss'],
})
export class CompanyDirectorQuickSetComponent implements OnChanges {
  /** Client ID the director belongs to. `null`/`0`/negative (brand-new,
   *  unsaved client) disables the widget entirely — there is nowhere to
   *  attach a `Person` row yet. */
  @Input() cliId: number | null = null;

  /** Director/CEO/owner name extracted from a company-register lookup
   *  result (`CompanyRegisterResult.director`). `null` means no signal —
   *  the widget renders nothing. */
  @Input() extractedDirectorName: string | null = null;

  /** The client's current contact persons, as already loaded by the host
   *  form (`EditClientComponent.persons`) — this component does not fetch
   *  its own copy. */
  @Input() existingPersons: Person[] | null = null;

  /** Emitted with the created/updated `Person` row every time this widget
   *  successfully writes one — the host (`edit-client.component.ts`) uses
   *  this to patch its own `existingPersons` copy and the separate
   *  "Сотрудники" tab (`ListClientPersonsComponent`, which loads its own
   *  copy independently and has no `[personList]` input) in place, rather
   *  than either going stale or needing a full server re-fetch. */
  @Output() personChanged = new EventEmitter<Person>();

  state: DirectorQuickSetState = 'none';
  confidence: MatchConfidence = 'confident';
  matchedDirector: Person | null = null;

  /** Picker state (`state === 'picker'` only). Options cover every existing
   *  person — not filtered to `PERSRESPONSIBLE >= 1` — plus a fixed
   *  "insert new" row. Name fields are pre-split from the extracted name
   *  but editable, same as the create-offer flow already requires. */
  pickerOptions: PickerOption[] = [];
  selectedPickerOption: PickerOption | null = null;
  pickerLName: string | null = null;
  pickerFName: string | null = null;
  pickerMName: string | null = null;

  /** Locally dismissed by the manager for the current extracted name; reset
   *  whenever a new lookup produces a different (or cleared) name. Never
   *  persisted — purely a "stop asking me about this one" UI convenience. */
  private dismissed: boolean = false;
  private lastSeenName: string | null = null;

  constructor(
    private cliApi: ClientApiService,
    private messageService: MessageService,
  ) { }

  ngOnChanges(_changes: SimpleChanges): void {
    this.recompute();
  }

  get visible(): boolean {
    return this.state !== 'none' && !this.dismissed;
  }

  /** Compact debug view of a `Person` for console output — just enough to
   *  tell people apart in the log without dumping every field. */
  private static describePerson(p: Person): any {
    return { PERSID: p.PERSID, PERSONLNAME: p.PERSONLNAME, PERSONFNAME: p.PERSONFNAME, PERSONMNAME: p.PERSONMNAME, PERSRESPONSIBLE: p.PERSRESPONSIBLE };
  }

  private recompute(): void {
    const name = this.extractedDirectorName?.trim() || null;
    console.log('*** CompanyDirectorQuickSetComponent in recompute: cliId=' + this.cliId
      + ' extractedDirectorName=' + JSON.stringify(name)
      + ' existingPersons.length=' + (this.existingPersons?.length ?? 0));

    if (name !== this.lastSeenName) {
      this.dismissed = false;
      this.lastSeenName = name;
    }

    if (!this.cliId || this.cliId <= 0 || !name) {
      this.state = 'none';
      this.confidence = 'confident';
      this.matchedDirector = null;
      console.log('*** CompanyDirectorQuickSetComponent in recompute: no cliId or no extracted name -- state=none');
      return;
    }

    const persons = this.existingPersons || [];
    const { lname: extractedSurname } = parseRegisterName(name);
    const flagged = persons.filter(p => p.PERSRESPONSIBLE === 2);
    const surnameMatches = persons.filter(p => normalizeForMatch(p.PERSONLNAME) === normalizeForMatch(extractedSurname));

    console.log('*** CompanyDirectorQuickSetComponent in recompute: extractedSurname=' + JSON.stringify(extractedSurname)
      + ' flagged=' + JSON.stringify(flagged.map(CompanyDirectorQuickSetComponent.describePerson))
      + ' surnameMatches=' + JSON.stringify(surnameMatches.map(CompanyDirectorQuickSetComponent.describePerson)));

    // Case A: nothing to conflict with.
    if (persons.length === 0) {
      this.confidence = 'confident';
      this.matchedDirector = null;
      this.state = 'create-offer';
      console.log('*** CompanyDirectorQuickSetComponent in recompute: Case A (no existing persons at all) -> confident / create-offer');
      return;
    }

    // Case B: exactly one flagged director, and they're the *only* person
    // whose surname matches the register — no "which Petrenko" ambiguity.
    if (flagged.length === 1 && surnameMatches.length === 1 && surnameMatches[0] === flagged[0]) {
      this.confidence = 'confident';
      this.matchedDirector = flagged[0];
      const currentName = [flagged[0].PERSONLNAME, flagged[0].PERSONFNAME, flagged[0].PERSONMNAME]
        .filter(part => part && part.trim())
        .join(' ');
      this.state = (normalizeForMatch(currentName) === normalizeForMatch(name)) ? 'confirmed' : 'update-offer';
      console.log('*** CompanyDirectorQuickSetComponent in recompute: Case B (sole flagged director, PERSID=' + flagged[0].PERSID + ') -> confident / ' + this.state
        + ' -- currentName=' + JSON.stringify(currentName) + ' vs registerName=' + JSON.stringify(name)
        + ' (normalized: ' + JSON.stringify(normalizeForMatch(currentName)) + ' vs ' + JSON.stringify(normalizeForMatch(name)) + ')');
      return;
    }

    // Case C: no flagged director, and no evidence anyone here is a near-match.
    if (flagged.length === 0 && surnameMatches.length === 0) {
      this.confidence = 'confident';
      this.matchedDirector = null;
      this.state = 'create-offer';
      console.log('*** CompanyDirectorQuickSetComponent in recompute: Case C (no flagged director, no surname match anywhere) -> confident / create-offer');
      return;
    }

    // Everything else is a real decision, not a guess: multiple flagged
    // directors, a flagged director whose surname doesn't match at all, a
    // duplicate surname across several persons, or a surname match sitting
    // on someone not currently flagged (the Problem 2 case AG-23d exists
    // to fix). Route to the picker.
    this.confidence = 'uncertain';
    this.matchedDirector = null;
    this.setupPicker(persons, name, surnameMatches);
    this.state = 'picker';
    console.log('*** CompanyDirectorQuickSetComponent in recompute: uncertain (flagged.length=' + flagged.length + ', surnameMatches.length=' + surnameMatches.length + ') -> picker'
      + ' -- preselected=' + (this.selectedPickerOption?.person ? JSON.stringify(CompanyDirectorQuickSetComponent.describePerson(this.selectedPickerOption.person)) : '"insert new"'));
  }

  private setupPicker(persons: Person[], extractedName: string, surnameMatches: Person[]): void {
    const insertNewOption: PickerOption = { label: '— Новий співробітник —', person: null };
    this.pickerOptions = [
      insertNewOption,
      ...persons.map(p => ({ label: this.formatPersonOption(p), person: p })),
    ];
    this.selectedPickerOption = (surnameMatches.length === 1)
      ? (this.pickerOptions.find(o => o.person === surnameMatches[0]) ?? insertNewOption)
      : insertNewOption;

    const { lname, fname, mname } = parseRegisterName(extractedName);
    this.pickerLName = lname;
    this.pickerFName = fname;
    this.pickerMName = mname;
  }

  private formatPersonOption(p: Person): string {
    const name = [p.PERSONLNAME, p.PERSONFNAME, p.PERSONMNAME].filter(part => part && part.trim()).join(' ');
    const responsibilityLabel = PersonResponcibilityList.find(r => r.index === p.PERSRESPONSIBLE)?.value;
    const extras = [p.PERSONTITLE, responsibilityLabel].filter(part => part && part.trim());
    return extras.length ? `${name} (${extras.join(', ')})` : name;
  }

  private buildBlankPerson(lname: string | null, fname: string | null, mname: string | null): Person {
    return {
      PERSID: -2,
      PERSCLIID: this.cliId,
      PERSONDEPT: null,
      PERSONIDCODE: null,
      PERSONFNAME: fname,
      PERSONMNAME: mname,
      PERSONLNAME: lname,
      PERSONPHONE: null,
      PERSONFAX: null,
      PERSONCPHONE: null,
      PERSONPHONE1: null,
      PERSONFAX1: null,
      PERSONNOTE: null,
      PERSONNOTE1: null,
      // Every path building a blank Person here sets PERSRESPONSIBLE = 2
      // (director) -- default the title to match, same as this codebase's
      // own PersonResponcibilityList already labels that value.
      PERSONTITLE: 'Директор',
      PERSONCOUNTRY: null,
      PERSONCITY: null,
      PERSONADDR1: null,
      PERSONADDR2: null,
      PERSONEMAIL: null,
      PERSONINDEX: null,
      PERSPASSWORD: null,
      PERSSESSIONID: null,
      PERSSESSTIME: null,
      PERSRESPONSIBLE: 2,
      PERSSIGNID: null,
    };
  }

  /**
   * Common "just wrote a director" tail for `onCreate()`/`onUpdate()`/
   * `onPickerConfirm()`'s two branches -- sets `matchedDirector`/`state`,
   * shows the success toast, and emits `personChanged`.
   *
   * Also demotes any OTHER person still flagged `PERSRESPONSIBLE = 2`
   * (`PERSRESPONSIBLE: 0`, "Нема") before doing any of that. `recompute()`'s
   * Case B requires exactly one flagged director to ever reach `'confirmed'`
   * -- so a leftover flagged person left over from before this write means
   * every future `recompute()` (triggered by the very `existingPersons`
   * change this write's own `personChanged` emit causes) finds
   * `flagged.length > 1` again and routes straight back to `'picker'`. The
   * write itself always succeeds in that case -- only the dialog looks
   * broken, re-offering the same choice forever. In practice this only ever
   * has something to demote from `onPickerConfirm()`: `onCreate()`/
   * `onUpdate()` only run when `confidence === 'confident'` (Case A/C or B),
   * which already means at most one flagged person existed going in.
   */
  private finalizeDirectorWrite(person: Person, successDetail: string): void {
    const others = (this.existingPersons || []).filter(p => p.PERSRESPONSIBLE === 2 && p.PERSID !== person.PERSID);
    if (others.length) {
      console.log('*** CompanyDirectorQuickSetComponent in finalizeDirectorWrite: demoting ' + others.length
        + ' other flagged director(s): ' + JSON.stringify(others.map(CompanyDirectorQuickSetComponent.describePerson)));
    }
    const demote$: Observable<Person[]> = others.length
      ? forkJoin(others.map(other => this.cliApi.putPersonById({ ...other, PERSRESPONSIBLE: 0 })))
      : of([]);

    demote$.subscribe({
      next: (demoted) => {
        this.matchedDirector = person;
        this.state = 'confirmed';
        this.messageService.add({ severity: 'success', summary: 'Директор', detail: successDetail });
        this.personChanged.emit(person);
        demoted.forEach(d => this.personChanged.emit(d));
      },
      error: (err) => {
        // The director write itself already succeeded -- still confirm.
        // Only the demotion failed, which leaves a real inconsistency
        // (two flagged directors), so surface it rather than pretend
        // nothing's wrong.
        console.log('*** CompanyDirectorQuickSetComponent in finalizeDirectorWrite: demotion failed: ' + JSON.stringify(err?.message || err));
        this.matchedDirector = person;
        this.state = 'confirmed';
        this.messageService.add({ severity: 'success', summary: 'Директор', detail: successDetail });
        this.messageService.add({ severity: 'warn', summary: 'Директор', detail: 'Не вдалося зняти позначку директора з попереднього співробітника — перевірте список вручну' });
        this.personChanged.emit(person);
      },
    });
  }

  /**
   * Defensive re-check for `onCreate()`/`onPickerConfirm()`'s "insert new"
   * branch, run against `existingPersons` right before POSTing a brand-new
   * `Person`: if someone with this exact full name is already there, treat
   * the write as already done rather than creating a second row.
   *
   * This is a safety net independent of *why* `onCreate()` gets called
   * again for a name that was already added -- a stale `existingPersons`
   * snapshot at click time, or the manager clicking "Додати як керівника"
   * again because the dialog didn't visibly confirm fast enough -- live
   * testing hit exactly this (three duplicate "Директор" rows from three
   * clicks). Whatever the trigger, this widget must never write two
   * `Person` rows for the same name.
   */
  private findExistingByName(lname: string | null, fname: string | null, mname: string | null): Person | null {
    return (this.existingPersons || []).find(p =>
      normalizeForMatch(p.PERSONLNAME) === normalizeForMatch(lname)
      && normalizeForMatch(p.PERSONFNAME) === normalizeForMatch(fname)
      && normalizeForMatch(p.PERSONMNAME) === normalizeForMatch(mname)
    ) || null;
  }

  onCreate(): void {
    const name = this.extractedDirectorName?.trim();
    if (!name || !this.cliId) { return; }

    const { lname, fname, mname } = parseRegisterName(name);

    const existing = this.findExistingByName(lname, fname, mname);
    if (existing) {
      console.log('*** CompanyDirectorQuickSetComponent in onCreate: a person named ' + JSON.stringify(name)
        + ' already exists (PERSID=' + existing.PERSID + ') -- updating/confirming instead of creating a duplicate');
      this.matchedDirector = existing;
      this.onUpdate();
      return;
    }

    const newPerson = this.buildBlankPerson(lname, fname, mname);
    this.state = 'saving';

    console.log('*** CompanyDirectorQuickSetComponent in onCreate: PERSCLIID=' + this.cliId
      + ' PERSONLNAME=' + JSON.stringify(lname) + ' PERSONFNAME=' + JSON.stringify(fname) + ' PERSONMNAME=' + JSON.stringify(mname));
    this.cliApi.postPersonById(newPerson).subscribe({
      next: (created) => {
        // routes/Person.ts's POST handler only ever returns { PERSID } --
        // not the full inserted row -- so `created` here carries no name/
        // PERSRESPONSIBLE data. Confirmed live: every field this widget
        // needs came back undefined, existingPersons kept looking like the
        // director was never added, and recompute() kept re-offering
        // create. Build the real record from what we know we sent
        // (newPerson) instead of trusting the sparse response for anything
        // but the new PERSID.
        const createdPerson: Person = { ...newPerson, PERSID: created.PERSID };
        console.log('*** CompanyDirectorQuickSetComponent in onCreate: created PERSID=' + createdPerson.PERSID);
        this.finalizeDirectorWrite(createdPerson, 'Керівника додано до списку співробітників');
      },
      error: (err) => {
        this.state = 'create-offer';
        console.log('*** CompanyDirectorQuickSetComponent in onCreate: postPersonById failed: ' + JSON.stringify(err?.message || err));
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося зберегти дані керівника' });
      },
    });
  }

  onUpdate(): void {
    const name = this.extractedDirectorName?.trim();
    if (!name || !this.matchedDirector) { return; }

    const { lname, fname, mname } = parseRegisterName(name);
    const updated: Person = {
      ...this.matchedDirector,
      PERSONLNAME: lname,
      PERSONFNAME: fname,
      PERSONMNAME: mname,
      // Only backfill when blank -- never overwrite a real existing title
      // (e.g. "Технічний директор") with the generic default.
      PERSONTITLE: this.matchedDirector.PERSONTITLE?.trim() || 'Директор',
    };
    this.state = 'saving';

    console.log('*** CompanyDirectorQuickSetComponent in onUpdate: PERSID=' + updated.PERSID
      + ' PERSONLNAME=' + JSON.stringify(lname) + ' PERSONFNAME=' + JSON.stringify(fname) + ' PERSONMNAME=' + JSON.stringify(mname));
    this.cliApi.putPersonById(updated).subscribe({
      next: () => {
        console.log('*** CompanyDirectorQuickSetComponent in onUpdate: updated PERSID=' + updated.PERSID);
        this.finalizeDirectorWrite(updated, 'Дані керівника оновлено');
      },
      error: (err) => {
        this.state = 'update-offer';
        console.log('*** CompanyDirectorQuickSetComponent in onUpdate: putPersonById failed: ' + JSON.stringify(err?.message || err));
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося оновити дані керівника' });
      },
    });
  }

  /** Confirm action for the `'picker'` state — "insert new" (no person
   *  selected) posts a fresh `Person`; an existing selection updates it,
   *  always setting `PERSRESPONSIBLE = 2` — picking someone here *means*
   *  "this is the director," whatever they were flagged as before. */
  onPickerConfirm(): void {
    if (!this.cliId) { return; }
    const lname = this.pickerLName?.trim() || null;
    const fname = this.pickerFName?.trim() || null;
    const mname = this.pickerMName?.trim() || null;
    console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: selected='
      + (this.selectedPickerOption?.person ? 'PERSID=' + this.selectedPickerOption.person.PERSID : '"insert new"')
      + ' lname=' + JSON.stringify(lname) + ' fname=' + JSON.stringify(fname) + ' mname=' + JSON.stringify(mname));
    if (!lname && !fname) { return; }

    this.state = 'saving';

    // "Insert new" but someone with this exact name is already in
    // existingPersons (e.g. a previous confirm already created them, and
    // this is a re-click) -- update/confirm them instead of writing a
    // second row. Same safety net as onCreate()'s findExistingByName().
    const target = this.selectedPickerOption?.person
      || (() => {
        const dup = this.findExistingByName(lname, fname, mname);
        if (dup) {
          console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: "insert new" but PERSID=' + dup.PERSID
            + ' already has this exact name -- updating instead of creating a duplicate');
        }
        return dup;
      })();

    if (!target) {
      const newPerson = this.buildBlankPerson(lname, fname, mname);
      console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: "insert new" branch -- posting new Person');
      this.cliApi.postPersonById(newPerson).subscribe({
        next: (created) => {
          // See onCreate()'s identical comment -- the backend's create
          // response only carries { PERSID }, so build the real record
          // from newPerson rather than trusting the response for anything
          // else.
          const createdPerson: Person = { ...newPerson, PERSID: created.PERSID };
          console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: created PERSID=' + createdPerson.PERSID);
          this.finalizeDirectorWrite(createdPerson, 'Керівника додано до списку співробітників');
        },
        error: (err) => {
          this.state = 'picker';
          console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: postPersonById failed: ' + JSON.stringify(err?.message || err));
          this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося зберегти дані керівника' });
        },
      });
    } else {
      const updated: Person = {
        ...target,
        PERSONLNAME: lname,
        PERSONFNAME: fname,
        PERSONMNAME: mname,
        PERSRESPONSIBLE: 2,
        // Only backfill when blank -- never overwrite a real existing title.
        PERSONTITLE: target.PERSONTITLE?.trim() || 'Директор',
      };
      console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: update branch -- PERSID=' + updated.PERSID + ' (was PERSRESPONSIBLE=' + target.PERSRESPONSIBLE + ', becomes 2)');
      this.cliApi.putPersonById(updated).subscribe({
        next: () => {
          console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: updated PERSID=' + updated.PERSID);
          this.finalizeDirectorWrite(updated, 'Дані керівника оновлено');
        },
        error: (err) => {
          this.state = 'picker';
          console.log('*** CompanyDirectorQuickSetComponent in onPickerConfirm: putPersonById failed: ' + JSON.stringify(err?.message || err));
          this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося оновити дані керівника' });
        },
      });
    }
  }

  onDismiss(): void {
    this.dismissed = true;
  }

  /**
   * Lets a host trigger whatever action this widget is currently offering,
   * from its own single confirm click, instead of requiring a second click
   * on this component's own buttons -- e.g. `edit-client.component.ts`'s
   * `onRegisterVerifyApply()` calls this right after a register-verify
   * "Оновити" click, so one confirm updates both `Clients` and `Person`.
   *
   * AG-23d: only auto-fires when `confidence === 'confident'` — an
   * uncertain match leaves the `'picker'` state rendered instead, waiting
   * for its own explicit confirm (`onPickerConfirm()`). A no-op in every
   * other case ('none'/'confirmed'/'saving'/uncertain) -- there is nothing
   * pending to confirm, a save is already in flight, or the manager still
   * needs to make a call this method must not make for them.
   */
  confirmPending(): void {
    console.log('*** CompanyDirectorQuickSetComponent in confirmPending: confidence=' + this.confidence + ' state=' + this.state);
    if (this.confidence !== 'confident') {
      console.log('*** CompanyDirectorQuickSetComponent in confirmPending: uncertain -- no auto-write, waiting for the picker\'s own confirm');
      return;
    }
    if (this.state === 'create-offer') {
      console.log('*** CompanyDirectorQuickSetComponent in confirmPending: auto-firing onCreate()');
      this.onCreate();
    } else if (this.state === 'update-offer') {
      console.log('*** CompanyDirectorQuickSetComponent in confirmPending: auto-firing onUpdate() for PERSID=' + this.matchedDirector?.PERSID);
      this.onUpdate();
    } else {
      console.log('*** CompanyDirectorQuickSetComponent in confirmPending: state=' + this.state + ' -- nothing pending, no-op');
    }
  }
}
